import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star } from 'lucide-react';
import { Button } from './ui/button';

export function RewardCard({ reward }) {
    const navigate = useNavigate();

    // Razorpay reward structure mapping
    // Prioritize brand name if the display name looks like test data or is missing
    const name = reward.brand?.name || reward.display_parameters?.name || reward.name;

    // Candidate images in priority order. The display_parameters.image_url is a
    // presigned URL that often expires (returns 403), while brand.logo_url is
    // stable. Try each in turn and fall back to a branded initial when all fail.
    const imageCandidates = [
        reward.display_parameters?.image_url,
        reward.brand?.logo_url,
    ].filter(Boolean);

    const [imgIndex, setImgIndex] = useState(0);
    const image = imageCandidates[imgIndex];
    const imageFailed = imgIndex >= imageCandidates.length;

    const handleImgError = () => setImgIndex((i) => i + 1);

    const initial = (name || '?').trim().charAt(0).toUpperCase();

    // Prices are in paise, convert to INR.
    // display_price is the admin-marked-up starting price (face value + margin);
    // prefer it when present. Otherwise fall through to the face value: fixed
    // cards carry denominations in eligible_fixed_denomination, range
    // (variable-amount) cards carry [min, max] in eligible_range_denomination.
    const startingPaise =
        reward.display_price
        ?? reward.eligible_fixed_denomination?.[0]
        ?? reward.eligible_range_denomination?.[0]
        ?? reward.amount
        ?? 0;
    const price = startingPaise / 100;

    const formatPrice = (value) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(value);
    };

    const handleClick = () => {
        // Navigate to a special path or handle reward selection
        // Given the user flow, we might need a RewardPage or reuse ProductPage with state
        navigate(`/product/reward_${reward.id}`, { state: { reward } });
    };

    return (
        <div
            onClick={handleClick}
            className="group cursor-pointer bg-white rounded-3xl overflow-hidden border border-gray-100 hover:shadow-xl transition-all duration-300"
        >
            <div className="relative aspect-square bg-white border-b border-gray-50 flex items-center justify-center overflow-hidden">
                {imageFailed ? (
                    <div className="w-full h-full flex flex-col items-center justify-center bg-surface gap-2 p-4">
                        <span className="flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 text-primary font-heading text-3xl">
                            {initial}
                        </span>
                        <span className="text-xs text-muted-foreground font-body text-center line-clamp-2">
                            {name}
                        </span>
                    </div>
                ) : (
                    <img
                        src={image}
                        alt={name}
                        onError={handleImgError}
                        className="max-w-full max-h-full object-contain p-4 group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                    />
                )}

                {/* Quick Buy Overlay */}
                <div className="absolute bottom-3 left-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <Button
                        className="w-full bg-primary hover:bg-primary/90 text-white rounded-full font-body text-sm"
                    >
                        Buy Now
                    </Button>
                </div>
            </div>

            <div className="p-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1 font-body">
                    Gift Card
                </p>
                <h3 className="font-body font-medium text-secondary mb-2 line-clamp-1 group-hover:text-primary transition-colors">
                    {name}
                </h3>

                <div className="flex items-center justify-between mt-auto">
                    <span className="font-body font-semibold text-secondary text-lg">
                        Starts @ {formatPrice(price)}
                    </span>
                    <div className="flex items-center gap-1">
                        <Star className="w-3 h-3 fill-warning text-warning" />
                        <span className="text-xs font-medium text-secondary">4.8</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
